import { useCallback, useEffect, useMemo, useRef, type MutableRefObject, type RefObject } from 'react';

const SMOOTH_SCROLL_DURATION_MS = 400;

export interface UseBizyChatScrollControllerOptions {
	/**
	 * Whether the idle blank slate (BIZY-683) is active. Mirrored into a ref
	 * so rAF callbacks scheduled in the same commit that activated it (e.g.
	 * the continuous-init bottom jump) see the fresh value.
	 */
	isBlankSlate: boolean;
}

export interface BizyChatScrollController {
	/** The scrollable timeline container. */
	containerRef: RefObject<HTMLDivElement>;
	/**
	 * "Scroll to the newest user turn when the next messages render lands."
	 * Exposed as a raw ref because useBizyExternalOpenAutoSend consumes it
	 * directly; prefer requestScrollToLatestMessage() elsewhere.
	 */
	shouldScrollRef: MutableRefObject<boolean>;
	requestScrollToLatestMessage: () => void;
	/**
	 * Smooth scroll with a controlled speed (plain `scrollTo({behavior:
	 * 'smooth'})` doesn't let us set the duration). Takes a target GETTER,
	 * re-read every frame: layout can shift while the animation runs (late
	 * timeline hydration, font reflow, async card content), and a target
	 * computed once at animation start then lands wrong. The final frame
	 * snaps exactly to the freshly-read target.
	 */
	animateScrollTo: (getScrollTarget: () => number) => void;
	cancelSmoothScroll: () => void;
	isSmoothScrolling: () => boolean;
	/**
	 * Instant bottom jump on the next frame (continuous-init load / finished
	 * loading a conversation). Skipped while the blank slate is active — its
	 * divider positioning is authoritative, and an instant bottom-jump would
	 * race (and could out-write) it. Checked at rAF time via the mirrored ref
	 * because the blank slate may activate in the same commit as the caller's
	 * effect.
	 */
	jumpToBottom: () => void;
}

/**
 * Owns the chat timeline's scroll mechanics: the shared smooth-scroll
 * animation and instant bottom jumps. The chat view composes these into its
 * intent-level effects (send-scroll, init jump, blank-slate anchoring).
 */
export function useBizyChatScrollController({ isBlankSlate }: UseBizyChatScrollControllerOptions): BizyChatScrollController {
	const containerRef = useRef<HTMLDivElement>(null);
	const shouldScrollRef = useRef(false);
	const isSmoothScrollingRef = useRef(false);
	const smoothScrollFrameRef = useRef<number | null>(null);
	const jumpFrameRef = useRef<number | null>(null);

	const isBlankSlateRef = useRef(isBlankSlate);
	isBlankSlateRef.current = isBlankSlate;

	const cancelSmoothScroll = useCallback(() => {
		if (smoothScrollFrameRef.current !== null) {
			cancelAnimationFrame(smoothScrollFrameRef.current);
			smoothScrollFrameRef.current = null;
		}
		isSmoothScrollingRef.current = false;
	}, []);

	// Unmount: stop any in-flight animation or pending jump.
	useEffect(() => {
		return () => {
			cancelSmoothScroll();
			if (jumpFrameRef.current !== null) cancelAnimationFrame(jumpFrameRef.current);
		};
	}, [cancelSmoothScroll]);

	const animateScrollTo = useCallback(
		(getScrollTarget: () => number) => {
			const container = containerRef.current;
			if (!container) return;
			cancelSmoothScroll();
			const startPos = container.scrollTop;
			let startTime: number | null = null;
			isSmoothScrollingRef.current = true;

			const step = (timestamp: number) => {
				if (!startTime) startTime = timestamp;
				const progress = Math.min((timestamp - startTime) / SMOOTH_SCROLL_DURATION_MS, 1);
				const eased = 1 - (1 - progress) ** 3;
				const scrollTarget = getScrollTarget();
				container.scrollTop = progress < 1 ? startPos + (scrollTarget - startPos) * eased : scrollTarget;
				if (progress < 1) {
					smoothScrollFrameRef.current = requestAnimationFrame(step);
				} else {
					smoothScrollFrameRef.current = null;
					isSmoothScrollingRef.current = false;
				}
			};
			smoothScrollFrameRef.current = requestAnimationFrame(step);
		},
		[cancelSmoothScroll]
	);

	const jumpToBottom = useCallback(() => {
		if (jumpFrameRef.current !== null) cancelAnimationFrame(jumpFrameRef.current);
		jumpFrameRef.current = requestAnimationFrame(() => {
			jumpFrameRef.current = null;
			if (isBlankSlateRef.current) return;
			const container = containerRef.current;
			if (container) container.scrollTo({ top: container.scrollHeight, behavior: 'instant' });
		});
	}, []);

	const requestScrollToLatestMessage = useCallback(() => {
		shouldScrollRef.current = true;
	}, []);

	const isSmoothScrolling = useCallback(() => isSmoothScrollingRef.current, []);

	return useMemo(
		() => ({
			containerRef,
			shouldScrollRef,
			requestScrollToLatestMessage,
			animateScrollTo,
			cancelSmoothScroll,
			isSmoothScrolling,
			jumpToBottom,
		}),
		[requestScrollToLatestMessage, animateScrollTo, cancelSmoothScroll, isSmoothScrolling, jumpToBottom]
	);
}
